<script lang="ts">
    import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons'
    import { onDestroy, tick } from 'svelte'
    import Fa from 'svelte-fa'
    import { fullscreenLayers, popoverIndex } from '../../stores'

    export let disabled = false
    export let labeled = false
    /** rótulo do campo. Só aparece na folha de tela inteira do celular, onde o rótulo do
        Zinput fica para trás (o expander vira position:fixed) e a tela ficaria sem dizer o
        que se está escolhendo. */
    export let titulo = ''
    export let htmlInput = null
    export let customTrigger = false

    export let open = false

    let openUpward = false
    let panelEl: HTMLDivElement = null

    const isMobile = window.api.utils.utils.isMobile()

    $: if (disabled) open = false

    // com o título na barra, o gatilho repetiria o valor escolhido sem necessidade — quem está
    // escolhendo vê a opção marcada na própria lista
    $: folhaComTitulo = isMobile && open && !!titulo

    export function close() {
        open = false
    }

    function toggle() {
        if (disabled) return
        open = !open
    }

    $: if (open) handleOpen()

    // No toque, o mesmo gesto que abre ainda gera um clique depois que a lista já está na tela — e
    // esse clique, caindo em cima do que acabou de aparecer, fechava tudo na hora (o campo parecia
    // não responder). Por isso, logo depois de abrir, os fechamentos "de fora" são ignorados por um
    // instante. Escolher uma opção não passa por aqui: usa o close() exportado, que nunca espera.
    const GESTURE_MS = 400
    let openedAt = 0

    function requestClose() {
        if (Date.now() - openedAt < GESTURE_MS) return
        open = false
    }

    async function handleOpen() {
        openedAt = Date.now()
        openUpward = false
        if (isMobile) {
            // a primeira medida do viewport sai antes de o painel existir no DOM (ver
            // startWatchingViewport). No iPhone isso pega o viewport num momento em que ele ainda
            // pode mudar — a barra do navegador recolhendo, a rolagem com inércia terminando — e a
            // camada fica com a altura errada até o próximo evento do visualViewport (o que o
            // usuário descobre dando zoom). Medir de novo no quadro seguinte à montagem corrige.
            await tick()
            requestAnimationFrame(updateViewportSize)
            return
        }
        await tick()
        adjustDirection()
    }

    function adjustDirection() {
        if (!htmlInput || !panelEl) return
        const triggerRect = htmlInput.getBoundingClientRect()
        const panelHeight = panelEl.getBoundingClientRect().height
        const spaceBelow = window.innerHeight - triggerRect.bottom
        const spaceAbove = triggerRect.top
        openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow
    }

    function onTriggerKeydown(ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault()
            toggle()
        } else if (ev.key === 'Escape') {
            open = false
        }
    }

    function clickOutside(node) {
        function handleClick(event) {
            if (open && !node.contains(event.target)) {
                requestClose()
            }
        }
        document.addEventListener('click', handleClick, true)
        return {
            destroy() {
                document.removeEventListener('click', handleClick, true)
            },
        }
    }

    // mantém o z-index acima de eventuais Popover em que este expander esteja aninhado.
    // não usa `use:` porque o container nunca é desmontado (só troca de aparência via CSS no mobile),
    // então uma action só rodaria uma vez e não acompanharia cada abre/fecha.
    let stackedIndex: number = null
    $: manageMobileStacking(isMobile && open)

    function manageMobileStacking(shouldStack: boolean) {
        if (shouldStack && stackedIndex === null) {
            popoverIndex.update((value) => {
                stackedIndex = value + 2
                return stackedIndex
            })
            // avisa quem está embaixo que a tela está coberta (ver popover.svelte)
            fullscreenLayers.update((value) => value + 1)
        } else if (!shouldStack && stackedIndex !== null) {
            stackedIndex = null
            popoverIndex.update((value) => (value > 1000 ? value - 1 : value))
            fullscreenLayers.update((value) => Math.max(0, value - 1))
        }
    }

    // no mobile o expander ocupa a tela inteira (posição fixa, 100dvh) pra aproveitar a área útil
    // pro usuário escolher a opção. só que 100dvh/100vh não descontam o teclado virtual — em boa
    // parte dos navegadores mobile o teclado é um overlay do sistema que não redimensiona o
    // viewport "de layout" (o que o CSS enxerga), só o viewport "visual". Resultado: com o teclado
    // aberto (ex.: usuário digitando um filtro), a parte de baixo do expander fica escondida atrás
    // dele. a VisualViewport API é a única forma confiável (cross-browser) de saber a altura
    // realmente visível — usamos ela pra encolher o expander via style inline (que tem prioridade
    // sobre o 100dvh do CSS) só enquanto ele está aberto no mobile. offsetTop entra junto porque no
    // iOS a página pode ser deslocada pra manter o campo focado visível acima do teclado, e um
    // elemento position:fixed não acompanha esse deslocamento sozinho.
    let viewportHeight: number = null
    let viewportOffsetTop = 0
    let watchingViewport = false

    function updateViewportSize() {
        if (!window.visualViewport) return
        viewportHeight = window.visualViewport.height
        viewportOffsetTop = window.visualViewport.offsetTop
    }

    function startWatchingViewport() {
        if (watchingViewport || !window.visualViewport) return
        watchingViewport = true
        updateViewportSize()
        window.visualViewport.addEventListener('resize', updateViewportSize)
        window.visualViewport.addEventListener('scroll', updateViewportSize)
        // o visualViewport não cobre tudo: girar o aparelho e a barra do navegador aparecendo ou
        // sumindo chegam como resize/orientationchange da janela
        window.addEventListener('resize', updateViewportSize)
        window.addEventListener('orientationchange', updateViewportSize)
    }

    function stopWatchingViewport() {
        if (!watchingViewport) return
        watchingViewport = false
        window.visualViewport.removeEventListener('resize', updateViewportSize)
        window.visualViewport.removeEventListener('scroll', updateViewportSize)
        window.removeEventListener('resize', updateViewportSize)
        window.removeEventListener('orientationchange', updateViewportSize)
        viewportHeight = null
        viewportOffsetTop = 0
    }

    $: if (isMobile) {
        if (open) startWatchingViewport()
        else stopWatchingViewport()
    }

    onDestroy(() => {
        stopWatchingViewport()
        // desmontar com a lista aberta (ex.: a tela inteira fechou junto) não pode deixar as contas penduradas
        if (stackedIndex !== null) {
            popoverIndex.update((value) => (value > 1000 ? value - 1 : value))
            fullscreenLayers.update((value) => Math.max(0, value - 1))
        }
    })
</script>

<div
    class="zinput-expander"
    class:zinput-expander_mobile-open={isMobile && open}
    style="{stackedIndex !== null ? `z-index:${stackedIndex};` : ''}{isMobile && open && viewportHeight !== null
        ? `top:${viewportOffsetTop}px; height:${viewportHeight}px;`
        : ''}"
    use:clickOutside
>
    <div class="zinput-expander_trigger-row" class:zinput-expander_sheet={folhaComTitulo}>
        {#if folhaComTitulo}
            <span class="zinput-expander_sheet-title">{titulo}</span>
        {/if}
        {#if customTrigger}
            <div class="zinput-expander_trigger-custom" bind:this={htmlInput}>
                <slot name="trigger" {open} />
            </div>
        {:else}
            <div
                class="zinput-expander_trigger zinput_input zinput_input_labeled-{labeled}"
                bind:this={htmlInput}
                role="button"
                tabindex={disabled ? -1 : 0}
                aria-disabled={disabled}
                on:click={toggle}
                on:keydown={onTriggerKeydown}
            >
                <slot name="trigger" {open} />
                {#if !(isMobile && open)}
                    <Fa class="zinput-expander_chevron" icon={faChevronDown} rotate={open ? 180 : 0} />
                {/if}
            </div>
        {/if}
        {#if isMobile && open}
            <button type="button" class="zinput-expander_mobile-close" on:click={requestClose} aria-label="Fechar">
                <Fa icon={faTimes} />
            </button>
        {/if}
    </div>
    {#if open}
        <div class="zinput-expander_panel" class:zinput-expander_panel-up={openUpward && !isMobile} bind:this={panelEl}>
            <slot name="panel" {open} {close} />
        </div>
    {/if}
</div>

<style>
    .zinput-expander {
        position: relative;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        /* sem isto o campo não encolhe abaixo do seu conteúdo: as etiquetas do multiselect não
           quebravam linha e empurravam o campo — e o contêiner da tela junto */
        min-width: 0;
    }

    .zinput-expander_trigger-row {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-grow: 1;
        min-width: 0;
    }

    .zinput-expander_trigger {
        display: flex;
        align-items: center;
        flex-grow: 1;
        /* ocupa toda a altura do campo: vazio, o conteúdo tem altura zero e o toque cairia fora da
           área clicável — chegando a acertar o botão ao lado (o "+" de cadastrar) */
        align-self: stretch;
        min-width: 0;
        cursor: pointer;
        user-select: none;
        gap: 8px;
    }

    .zinput-expander_trigger-custom {
        display: flex;
        flex-grow: 1;
        min-width: 0;
    }

    .zinput-expander_trigger[aria-disabled='true'] {
        cursor: not-allowed;
    }

    .zinput-expander_trigger :global(.zinput-expander_chevron) {
        flex-shrink: 0;
        transition: transform 0.15s ease;
        font-size: 10px;
        padding-right: 2px;
        margin-top: -14px;
        margin-left: auto;
        opacity: 0.7;
    }

    /* cores e cantos vêm das variáveis dos campos (css_z_field.css), então a lista tem a mesma
       paleta do campo que a abriu. O fundo é opaco de propósito: ela flutua sobre a tela. */
    .zinput-expander_panel {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background-color: var(--z-field-solid-bg);
        border: 1px solid var(--z-field-border);
        border-radius: var(--z-field-radius);
        box-shadow: 0px 10px 24px #00000040;
        z-index: 5;
        max-height: 320px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .zinput-expander_panel-up {
        top: auto;
        bottom: 100%;
        margin-top: 0;
        margin-bottom: 4px;
    }

    .zinput-expander_mobile-open {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        /* aqui a lista é o fundo da tela, não um painel flutuando sobre ela: o tom é o das folhas
           do app (ver --z-field-sheet-bg) */
        background-color: var(--z-field-sheet-bg);
    }

    .zinput-expander_mobile-open .zinput-expander_trigger-row {
        flex: 0 0 auto;
        padding: 10px 8px 10px 12px;
        background-color: var(--z-field-bg);
        border-bottom: 1px solid var(--z-field-border);
    }

    .zinput-expander_sheet-title {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 17px;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .zinput-expander_sheet .zinput-expander_trigger {
        display: none;
    }

    .zinput-expander_mobile-close {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--z-field-act-size);
        height: var(--z-field-act-size);
        border: 0;
        border-radius: var(--z-field-act-radius);
        background-color: var(--z-field-act-bg);
        color: inherit;
        cursor: pointer;
    }

    .zinput-expander_mobile-close:hover {
        background-color: var(--z-field-act-bg-hover);
    }

    .zinput-expander_mobile-open .zinput-expander_panel {
        position: static;
        flex: 1 1 auto;
        margin: 0;
        border: 0;
        border-radius: 0;
        max-height: none;
        background-color: transparent;
        overscroll-behavior: contain;
        /* a última opção não pode cair atrás da barra de gestos do aparelho */
        padding-bottom: env(safe-area-inset-bottom, 0px);
    }
</style>
